import { VStack, Span } from "@chakra-ui/react"
import SearchCombobox from "./SearchCombobox"
import { useMemo } from "react"
import type { CustomerItem } from "@hydrologiq/shared/components/activities/inputs/CustomersOnlyCombobox"

export type CustomerSiteItem = {
  id: string
  name: string
}

interface CustomerSitesComboboxProps {
  customer?: CustomerItem | null
  onItemSelection?: (value: CustomerSiteItem | undefined) => void
  initialValue?: CustomerSiteItem
  value?: CustomerSiteItem | null
  disabled?: boolean
  required?: boolean
  CreateAction?: () => React.ReactNode
}

/**
 * Combobox for selecting sites for a specific customer.
 * Requires a customer to be selected first.
 * Uses static items from the customer object instead of API calls.
 */
const CustomerSitesCombobox = ({
  customer,
  onItemSelection,
  initialValue,
  value,
  disabled = false,
  required,
  CreateAction,
}: CustomerSitesComboboxProps) => {
  // Convert customer sites to the format expected by SearchCombobox
  const staticSites = useMemo(() => {
    if (!customer || !customer.sites) return []
    return customer.sites.map((site) => ({
      id: site.id,
      name: site.name,
    }))
  }, [customer])

  const isDisabled = disabled || !customer

  return (
    <SearchCombobox<CustomerSiteItem>
      queryType="sites"
      staticItems={staticSites}
      onItemSelection={onItemSelection}
      label="sites"
      formatSelectedItem={(item) => item.name}
      initialValue={initialValue}
      value={value}
      disabled={isDisabled}
      required={required}
      loadInitialItems={false} // Don't load from API, use static items
      itemContent={(item) => (
        <VStack gap={0} width="100%" align="start">
          <Span width="100%" textStyle="sm" fontWeight="medium">
            {item.name}
          </Span>
        </VStack>
      )}
      CreateAction={CreateAction}
    />
  )
}

export default CustomerSitesCombobox
